'use client';

import React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

// ============================================================================
// Styles (inline - no Tailwind CSS dependency)
// ============================================================================

const MENU_STYLES = {
  content: {
    base: {
      zIndex: 1500, // popover level - must be above modal (1400)
      minWidth: '8rem',
      overflow: 'hidden',
      borderRadius: '6px',
      border: '1px solid #E5E7EB',
      backgroundColor: '#FFFFFF',
      padding: '4px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    } as React.CSSProperties,
  },
  item: {
    base: {
      position: 'relative',
      display: 'flex',
      cursor: 'pointer',
      userSelect: 'none',
      alignItems: 'center',
      gap: '8px',
      borderRadius: '4px',
      padding: '6px 8px',
      fontSize: '14px',
      outline: 'none',
      transition: 'background-color 150ms ease',
      color: '#1F2937',
    } as React.CSSProperties,
    default: {
      backgroundColor: 'transparent',
    } as React.CSSProperties,
    defaultHover: {
      backgroundColor: '#F3F4F6',
    } as React.CSSProperties,
    destructive: {
      color: '#DC2626',
    } as React.CSSProperties,
    destructiveHover: {
      backgroundColor: '#FEE2E2',
    } as React.CSSProperties,
  },
  separator: {
    marginLeft: '-4px',
    marginRight: '-4px',
    marginTop: '4px',
    marginBottom: '4px',
    height: '1px',
    backgroundColor: '#E5E7EB',
  } as React.CSSProperties,
  label: {
    padding: '6px 8px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
  } as React.CSSProperties,
};

// Menu Root
export const MenuRoot = PopoverPrimitive.Root;

// Menu Trigger
export const MenuTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>
>(({ children, asChild = true, ...props }, ref) => (
  <PopoverPrimitive.Trigger ref={ref} asChild={asChild} {...props}>
    {children}
  </PopoverPrimitive.Trigger>
));
MenuTrigger.displayName = 'MenuTrigger';

// Menu Positioner (Portal)
export const MenuPositioner = PopoverPrimitive.Portal;

// Menu Content
export interface MenuContentProps
  extends Omit<React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>, 'align'> {
  minWidth?: string;
  align?: 'start' | 'center' | 'end';
}

export const MenuContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  MenuContentProps
>(({ align = 'center', sideOffset = 4, minWidth, style, ...props }, ref) => {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        style={{
          ...MENU_STYLES.content.base,
          minWidth: minWidth || '200px',
          ...style,
        }}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
});
MenuContent.displayName = 'MenuContent';

// Menu Item
export interface MenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  variant?: 'default' | 'destructive';
}

export const MenuItem = React.forwardRef<HTMLDivElement, MenuItemProps>(
  ({ variant = 'default', style, onMouseEnter, onMouseLeave, ...props }, ref) => {
    const [isHovered, setIsHovered] = React.useState(false);

    const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
      setIsHovered(true);
      onMouseEnter?.(e);
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
      setIsHovered(false);
      onMouseLeave?.(e);
    };

    const itemStyle: React.CSSProperties = {
      ...MENU_STYLES.item.base,
      ...(variant === 'destructive' ? MENU_STYLES.item.destructive : MENU_STYLES.item.default),
      ...(isHovered
        ? variant === 'destructive'
          ? MENU_STYLES.item.destructiveHover
          : MENU_STYLES.item.defaultHover
        : {}),
      ...style,
    };

    return (
      <div
        ref={ref}
        style={itemStyle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      />
    );
  }
);
MenuItem.displayName = 'MenuItem';

// Menu Separator
export const MenuSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ style, ...props }, ref) => (
    <div ref={ref} style={{ ...MENU_STYLES.separator, ...style }} {...props} />
  )
);
MenuSeparator.displayName = 'MenuSeparator';

// Menu Label
export const MenuLabel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ style, ...props }, ref) => (
    <div ref={ref} style={{ ...MENU_STYLES.label, ...style }} {...props} />
  )
);
MenuLabel.displayName = 'MenuLabel';

// Export as namespace for easier imports
export const Menu = {
  Root: MenuRoot,
  Trigger: MenuTrigger,
  Positioner: MenuPositioner,
  Content: MenuContent,
  Item: MenuItem,
  Separator: MenuSeparator,
  Label: MenuLabel,
};

// Legacy exports for compatibility
export const MenuButton = MenuTrigger;
export const MenuList = MenuContent;
